import type {
  MediaAnalysisState,
  AccountSession,
  ArchiveUploadResponse,
  AuthResponse,
  ConnectionTestResult,
  CreateConnectionInput,
  CreateDatasetInput,
  CreatedWorkspaceUser,
  CreatePermissionInput,
  CreateUserInput,
  DataSource,
  DatasetSchema,
  IngestionJob,
  JobListItem,
  JobListResponse,
  MediaJob,
  MediaListQuery,
  MediaUploadKind,
  MediaUploadResponse,
  MfaChallenge,
  PaginatedResponse,
  PasswordResetRequestResult,
  PermissionConfiguration,
  ProcessedDocumentPreview,
  RevealFieldInput,
  RevealFieldResult,
  SchemaFieldDefinition,
  Session,
  SessionListQuery,
  SourceConnection,
  TenantOption,
  UpdateConnectionInput,
  UpdatePermissionInput,
  UpdateUserInput,
  UserListQuery,
  UserListResponse,
  UserOption,
  VaultDataset,
  VaultRecordItem,
  WorkspaceUser,
  AcquisitionNotificationItem,
  AgreementAcceptanceRequest,
  AgreementAcceptanceResponse,
  AgreementResponse,
  AgreementStatusResponse,
  ClientCatalogItem,
  ClientPublicationResponse,
  FreelancerSubmissionResponse,
  GlobalSearchResponse,
  PublishToClientRequest,
  QcReviewResponse,
  QcReviewSubmitRequest,
  RightsVerificationRequest,
  RightsVerificationResponse,
  SubmissionCreateResponse,
  SubmissionVersionResponse,
  VersionAnalysisResponse,
  AiSettings,
  AiSettingsInput,
  AiSettingsTestResult,
  UploadSubmissionMetadata,
  RetailStore,
  RetailAsset,
  RetailCatalog,
  RetailPrivacyInspection,
  LabelContract,
  SamplingCoverage,
  TaskBatchResponse,
  DatasetReleaseItem,
} from '../types';
import { cachedRequest, invalidateCache, setCachedValue, updateCachedValues } from './cache';
export { getCachedValue } from './cache';

const baseUrl = import.meta.env.VITE_API_URL || '/api';
const CACHE_TTL = {
  session: 30_000,
  jobs: 30_000,
  datasets: 30_000,
  users: 60_000,
  permissions: 120_000,
  connectors: 120_000,
  connections: 30_000,
  vault: 30_000,
} as const;
export const AUTH_EXPIRED_EVENT = 'privacy-factory:auth-expired';
let authenticationGeneration = 0;

export class ApiError extends Error {
  constructor(
    message: string,
    public code = 'REQUEST_FAILED',
    public status = 500,
    public retryAfterMs?: number,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

function authHeaders(): Record<string, string> {
  const accessToken = sessionStorage.getItem('privacy_factory_access_token') || (typeof localStorage !== 'undefined' ? localStorage.getItem('privacy_factory_access_token') : null);
  const tenantId = sessionStorage.getItem('privacy_factory_tenant_id') || (typeof localStorage !== 'undefined' ? localStorage.getItem('privacy_factory_tenant_id') : null);
  return {
    ...(accessToken ? { authorization: `Bearer ${accessToken}` } : {}),
    ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
  };
}

function isJwtExpired(token: string | null): boolean {
  if (!token) return true;
  try {
    const parts = token.split('.');
    if (parts.length !== 3) return false;
    const base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
    const jsonStr = atob(base64);
    const payload = JSON.parse(jsonStr) as { exp?: number };
    if (typeof payload.exp !== 'number') return false;
    return payload.exp * 1000 <= Date.now() + 5000;
  } catch {
    return false;
  }
}

function saveAuthentication(result: AuthResponse, replaceSession = false): AuthResponse {
  if (replaceSession) authenticationGeneration += 1;
  sessionStorage.setItem('privacy_factory_access_token', result.accessToken);
  sessionStorage.setItem('privacy_factory_tenant_id', result.session.tenantId);
  try {
    localStorage.setItem('privacy_factory_access_token', result.accessToken);
    localStorage.setItem('privacy_factory_tenant_id', result.session.tenantId);
  } catch {
    // Storage can be unavailable in hardened/private browser contexts.
  }
  return result;
}

function clearAuthentication(): void {
  authenticationGeneration += 1;
  sessionStorage.removeItem('privacy_factory_access_token');
  sessionStorage.removeItem('privacy_factory_tenant_id');
  try {
    localStorage.removeItem('privacy_factory_access_token');
    localStorage.removeItem('privacy_factory_tenant_id');
  } catch {
    // Storage can be unavailable in hardened/private browser contexts.
  }
}

function expireAuthentication(): void {
  clearAuthentication();
  invalidateCache();
  window.dispatchEvent(new Event(AUTH_EXPIRED_EVENT));
}

async function parseError(response: Response): Promise<ApiError> {
  const body = await response.json().catch(() => ({}));
  const retryAfterMs = parseRetryAfter(response.headers.get('retry-after'));
  const rateLimitMessage = retryAfterMs
    ? `Too many requests. Try again in ${Math.max(1, Math.ceil(retryAfterMs / 1000))} seconds.`
    : 'Too many requests. Please wait a moment and try again.';
  return new ApiError(
    response.status === 429 ? rateLimitMessage : body.error?.message || 'The request failed.',
    body.error?.code || (response.status === 429 ? 'RATE_LIMITED' : 'REQUEST_FAILED'),
    response.status,
    retryAfterMs,
  );
}

let refreshRequest: Promise<AuthResponse> | undefined;

async function refreshAuthentication(): Promise<AuthResponse> {
  if (!refreshRequest) {
    const generation = authenticationGeneration;
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const timeout = controller ? setTimeout(() => controller.abort(), 15000) : null;
    refreshRequest = fetch(`${baseUrl}/auth/refresh`, {
      method: 'POST',
      credentials: 'include',
      headers: { 'content-type': 'application/json' },
      signal: controller?.signal,
    }).then(async (response) => {
      if (timeout) clearTimeout(timeout);
      if (!response.ok) throw await parseError(response);
      if (generation !== authenticationGeneration) {
        throw new ApiError('The authentication state changed while the session was refreshing.', 'AUTH_STATE_CHANGED', 401);
      }
      return saveAuthentication(await response.json() as AuthResponse);
    }).catch((err) => {
      if (timeout) clearTimeout(timeout);
      throw err;
    }).finally(() => { refreshRequest = undefined; });
  }
  return refreshRequest;
}

async function request<T>(path: string, init?: RequestInit, retry = true): Promise<T> {
  const generation = authenticationGeneration;
  const initialAccessToken = sessionStorage.getItem('privacy_factory_access_token');
  const bodySetsContentType = init?.body instanceof Blob || (typeof FormData !== 'undefined' && init?.body instanceof FormData);
  const response = await fetch(`${baseUrl}${path}`, {
    ...init,
    credentials: 'include',
    headers: {
      ...(bodySetsContentType ? {} : { 'content-type': 'application/json' }),
      ...authHeaders(),
      ...init?.headers,
    },
  });
  if (response.status === 401 && retry && !path.startsWith('/auth/')) {
    const unauthorized = await parseError(response.clone());
    if (['AUTHENTICATION_REQUIRED', 'INVALID_TOKEN', 'SESSION_EXPIRED'].includes(unauthorized.code)) {
      const currentAccessToken = sessionStorage.getItem('privacy_factory_access_token');
      // If another concurrent request already refreshed the access token while this request was running,
      // retry immediately with the updated token without initiating a redundant refresh.
      if (currentAccessToken && initialAccessToken && currentAccessToken !== initialAccessToken) {
        return request<T>(path, init, false);
      }
      try {
        await refreshAuthentication();
        return request<T>(path, init, false);
      } catch (err: unknown) {
        if (err instanceof ApiError && (err.status === 401 || err.status === 403)) {
          expireAuthentication();
        }
      }
    }
  }
  if (!response.ok) throw await parseError(response);
  if (generation !== authenticationGeneration) {
    throw new ApiError('The authentication state changed while the request was in progress.', 'AUTH_STATE_CHANGED', 401);
  }
  if (response.status === 204) return undefined as T;
  const body = await response.json().catch(() => ({}));
  return body;
}

async function publicPost<T>(path: string, body: object): Promise<T> {
  const response = await fetch(`${baseUrl}${path}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw await parseError(response);
  if (response.status === 204) return undefined as T;
  return await response.json() as T;
}

async function authenticate(path: string, body: object): Promise<AuthResponse> {
  const generation = authenticationGeneration;
  const result = await publicPost<AuthResponse>(path, body);
  if (generation !== authenticationGeneration) {
    throw new ApiError('The authentication state changed while signing in.', 'AUTH_STATE_CHANGED', 401);
  }
  return saveAuthentication(result, true);
}

function uploadMediaFile(
  file: File,
  kind: MediaUploadKind,
  onProgress?: (progress: number) => void,
  retry = true,
  targetTenantId?: string,
  metadata?: UploadSubmissionMetadata,
): Promise<MediaUploadResponse | ArchiveUploadResponse> {
  return new Promise((resolve, reject) => {
    const generation = authenticationGeneration;
    const upload = new XMLHttpRequest();
    const uploadPath = kind === 'audio-archive' ? 'archive/upload' : `${kind}/upload`;
    const queryParams = targetTenantId ? `?target_tenant_id=${encodeURIComponent(targetTenantId)}` : '';
    upload.open('POST', `${baseUrl}/${uploadPath}${queryParams}`);
    upload.withCredentials = true;
    for (const [name, value] of Object.entries(authHeaders())) upload.setRequestHeader(name, value);
    upload.setRequestHeader('Idempotency-Key', crypto.randomUUID());
    if (targetTenantId) {
      upload.setRequestHeader('X-Target-Tenant-Id', targetTenantId);
    }
    upload.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        // Reserve the final 5% for the server response. Browsers report 100%
        // as soon as request bytes leave the client, which previously made a
        // still-validating request look successfully completed.
        onProgress?.(Math.min(95, Math.round(event.loaded / event.total * 95)));
      }
    });
    upload.addEventListener('load', async () => {
      if (generation !== authenticationGeneration) {
        reject(new ApiError('The authentication state changed while the upload was in progress.', 'AUTH_STATE_CHANGED', 401));
        return;
      }
      const body = parseResponse(upload.responseText);
      if (upload.status >= 200 && upload.status < 300) {
        onProgress?.(100);
        resolve(body as unknown as MediaUploadResponse);
        return;
      }

      if (upload.status === 401 && retry) {
        const errorCode = body.error?.code || '';
        if (['AUTHENTICATION_REQUIRED', 'INVALID_TOKEN', 'SESSION_EXPIRED'].includes(errorCode)) {
          try {
            await refreshAuthentication();
            resolve(await uploadMediaFile(file, kind, onProgress, false, targetTenantId, metadata));
            return;
          } catch {
            expireAuthentication();
          }
        }
      }

      const retryAfterMs = parseRetryAfter(upload.getResponseHeader('retry-after'));
      const message = upload.status === 429
        ? retryAfterMs
          ? `Too many requests. Try again in ${Math.max(1, Math.ceil(retryAfterMs / 1000))} seconds.`
          : 'Too many requests. Please wait a moment and try again.'
        : body.error?.message || 'The request failed.';
      reject(new ApiError(message, body.error?.code || (upload.status === 429 ? 'RATE_LIMITED' : 'REQUEST_FAILED'), upload.status, retryAfterMs));
    });
    upload.addEventListener('error', () => reject(new ApiError('The upload could not reach the server.', 'NETWORK_ERROR', 0)));
    const form = new FormData();
    form.append('file', file, file.name);
    if (metadata) {
      form.append('title', metadata.title);
      form.append('category', metadata.category);
      form.append('description', metadata.description);
      if (metadata.totalItems) form.append('total_items', metadata.totalItems);
      if (metadata.declaredFileSize) form.append('declared_file_size', metadata.declaredFileSize);
      if (metadata.region) form.append('region', metadata.region);
      if (metadata.languages) form.append('languages', metadata.languages);
      if (metadata.labelingStatus) form.append('labeling_status', metadata.labelingStatus);
      if (metadata.usageRights) form.append('usage_rights', metadata.usageRights);
    }
    upload.send(form);
  });
}

function uploadAcquisitionSubmission(
  formData: FormData,
  onProgress?: (progress: number) => void,
  retry = true,
): Promise<SubmissionCreateResponse> {
  return new Promise((resolve, reject) => {
    const generation = authenticationGeneration;
    const upload = new XMLHttpRequest();
    upload.open('POST', `${baseUrl}/acquisition/submissions`);
    upload.withCredentials = true;
    for (const [name, value] of Object.entries(authHeaders())) upload.setRequestHeader(name, value);
    upload.setRequestHeader('Idempotency-Key', crypto.randomUUID());
    upload.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) onProgress?.(Math.min(95, Math.round(event.loaded / event.total * 95)));
    });
    upload.addEventListener('load', async () => {
      if (generation !== authenticationGeneration) {
        reject(new ApiError('The authentication state changed while the upload was in progress.', 'AUTH_STATE_CHANGED', 401));
        return;
      }
      const body = parseResponse(upload.responseText);
      if (upload.status >= 200 && upload.status < 300) {
        onProgress?.(100);
        invalidateCache('acquisition:submissions:mine');
        invalidateCache('acquisition:superadmin:queue');
        invalidateCache('acquisition:qc:queue');
        resolve(body as unknown as SubmissionCreateResponse);
        return;
      }
      if (upload.status === 401 && retry) {
        const errorCode = body.error?.code || '';
        if (['AUTHENTICATION_REQUIRED', 'INVALID_TOKEN', 'SESSION_EXPIRED'].includes(errorCode)) {
          try {
            await refreshAuthentication();
            resolve(await uploadAcquisitionSubmission(formData, onProgress, false));
            return;
          } catch {
            expireAuthentication();
          }
        }
      }
      const retryAfterMs = parseRetryAfter(upload.getResponseHeader('retry-after'));
      const message = upload.status === 429
        ? retryAfterMs
          ? `Too many requests. Try again in ${Math.max(1, Math.ceil(retryAfterMs / 1000))} seconds.`
          : 'Too many requests. Please wait a moment and try again.'
        : body.error?.message || 'The QC submission failed.';
      reject(new ApiError(message, body.error?.code || (upload.status === 429 ? 'RATE_LIMITED' : 'REQUEST_FAILED'), upload.status, retryAfterMs));
    });
    upload.addEventListener('error', () => reject(new ApiError('The upload could not reach the server.', 'NETWORK_ERROR', 0)));
    upload.send(formData);
  });
}

function parseResponse(value: string): { error?: { message?: string; code?: string } } & Record<string, unknown> {
  try { return JSON.parse(value) as { error?: { message?: string; code?: string } } & Record<string, unknown>; }
  catch { return {}; }
}

function listPath(path: string, query: MediaListQuery | SessionListQuery | UserListQuery): string {
  const parameters = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (key === 'force' || value === undefined || value === '') continue;
    const apiKey = key === 'pageSize' ? 'page_size'
      : key === 'sortBy' ? 'sort_by'
        : key === 'sortDirection' ? 'sort_direction'
          : key === 'mediaType' ? 'media_type'
            : key === 'datasetId' ? 'dataset_id'
              : key === 'includeManagerOptions' ? 'include_manager_options'
              : key;
    parameters.set(apiKey, String(value));
  }
  const encoded = parameters.toString();
  return encoded ? `${path}?${encoded}` : path;
}

async function processedMediaResponse(
  id: string,
  retry = true,
  artifact: 'download' | 'dataset-download' = 'download',
): Promise<Response> {
  const generation = authenticationGeneration;
  const response = await fetch(`${baseUrl}/processed-media/${id}/${artifact}?proxy=true`, {
    credentials: 'include',
    headers: authHeaders(),
  });
  if (response.status === 401 && retry) {
    try {
      await refreshAuthentication();
      return processedMediaResponse(id, false, artifact);
    } catch {
      expireAuthentication();
    }
  }
  if (!response.ok) throw await parseError(response);
  if (generation !== authenticationGeneration) {
    throw new ApiError('The authentication state changed while the download was in progress.', 'AUTH_STATE_CHANGED', 401);
  }
  return response;
}

export const api = {
  async globalSearch(query: string, limit = 24): Promise<GlobalSearchResponse> {
    const params = new URLSearchParams({ q: query, limit: String(limit) });
    return request<GlobalSearchResponse>(`/search?${params.toString()}`);
  },
  async mediaMetadata(jobId: string): Promise<MediaAnalysisState> {
    return request<{ data: MediaAnalysisState }>(`/jobs/${encodeURIComponent(jobId)}/metadata`).then(r => r.data);
  },
  async extractMediaMetadata(jobId: string): Promise<MediaAnalysisState> {
    return request<{ data: MediaAnalysisState }>(`/jobs/${encodeURIComponent(jobId)}/metadata`, { method: 'POST' }).then(r => r.data);
  },
  async runMediaQc(jobId: string): Promise<MediaAnalysisState> {
    return request<{ data: MediaAnalysisState }>(`/jobs/${encodeURIComponent(jobId)}/qc`, { method: 'POST' }).then(r => r.data);
  },
  async downloadMediaReport(jobId: string, kind: 'metadata' | 'qc', filename: string): Promise<void> {
    const report = await request<Record<string, unknown>>(`/jobs/${encodeURIComponent(jobId)}/reports/${kind}`);
    const url = URL.createObjectURL(new Blob([JSON.stringify(report, null, 2)], { type: 'application/json' }));
    const link = document.createElement('a');
    link.href = url;
    link.download = `${filename.replace(/[^a-zA-Z0-9._-]/g, '_')}.${kind}.json`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  },
  async restoreSession(): Promise<Session> {
    return cachedRequest('session', async () => {
      const storedToken = sessionStorage.getItem('privacy_factory_access_token') || (typeof localStorage !== 'undefined' ? localStorage.getItem('privacy_factory_access_token') : null);
      if (!storedToken || isJwtExpired(storedToken)) {
        try {
          return (await refreshAuthentication()).session;
        } catch (cause) {
          // Local development bypass is enforced by the backend, never by the browser.
          if (cause instanceof ApiError && cause.status === 401 && cause.code !== 'AUTH_STATE_CHANGED') {
            return request<{ data: Session }>('/session', undefined, false).then(result => result.data);
          }
          throw cause;
        }
      }
      return request<{ data: Session }>('/session').then((result) => result.data);
    }, CACHE_TTL.session);
  },
  async login(email: string, password: string): Promise<Session | MfaChallenge> {
    const generation = authenticationGeneration;
    const result = await publicPost<AuthResponse | MfaChallenge>('/auth/login', { email, password });
    if (generation !== authenticationGeneration) {
      throw new ApiError('The authentication state changed while signing in.', 'AUTH_STATE_CHANGED', 401);
    }
    if ('mfaRequired' in result) return result;
    const session = saveAuthentication(result, true).session;
    invalidateCache();
    setCachedValue('session', session, CACHE_TTL.session);
    return session;
  },
  async verifyMfa(challengeId: string, code: string): Promise<Session> {
    const session = (await authenticate('/auth/mfa/verify', { challengeId, code })).session;
    invalidateCache();
    setCachedValue('session', session, CACHE_TTL.session);
    return session;
  },
  async resendMfa(challengeId: string): Promise<MfaChallenge> {
    return publicPost<MfaChallenge>('/auth/mfa/resend', { challengeId });
  },
  async requestPasswordReset(email: string): Promise<PasswordResetRequestResult> {
    return publicPost<PasswordResetRequestResult>('/auth/password/forgot', { email });
  },
  async resetPassword(token: string, newPassword: string): Promise<void> {
    await publicPost('/auth/password/reset', { token, newPassword });
  },
  async logout(): Promise<void> {
    clearAuthentication();
    invalidateCache();
    const logoutRequest = fetch(`${baseUrl}/auth/logout`, {
      method: 'POST',
      credentials: 'include',
      keepalive: true,
    });
    const response = await logoutRequest;
    if (!response.ok) throw await parseError(response);
  },
  async session(force = false): Promise<Session> {
    return cachedRequest('session', () => request<{ data: Session }>('/session').then((result) => result.data), CACHE_TTL.session, force);
  },
  async updateProfile(displayName: string): Promise<Session> {
    const result = await request<{ data: Session }>('/account/profile', { method: 'PATCH', body: JSON.stringify({ displayName }) });
    setCachedValue('session', result.data, CACHE_TTL.session);
    return result.data;
  },
  async changePassword(currentPassword: string, newPassword: string): Promise<Session> {
    const result = await request<AuthResponse>('/account/password', { method: 'PATCH', body: JSON.stringify({ currentPassword, newPassword }) });
    const session = saveAuthentication(result, true).session;
    invalidateCache();
    setCachedValue('session', session, CACHE_TTL.session);
    return session;
  },
  async setupMfa(currentPassword: string): Promise<MfaChallenge> {
    return request<MfaChallenge>('/account/mfa/setup', { method: 'POST', body: JSON.stringify({ currentPassword }) });
  },
  async confirmMfa(challengeId: string, code: string): Promise<void> {
    await request('/account/mfa/confirm', { method: 'POST', body: JSON.stringify({ challengeId, code }) });
    invalidateCache('session');
  },
  async disableMfa(currentPassword: string): Promise<void> {
    await request('/account/mfa/disable', { method: 'POST', body: JSON.stringify({ currentPassword }) });
    invalidateCache('session');
  },
  async accountSessions(query: SessionListQuery = {}): Promise<PaginatedResponse<AccountSession>> {
    const path = listPath('/account/sessions', query);
    return cachedRequest(`account-sessions:${path}`, () => request<PaginatedResponse<AccountSession>>(path), CACHE_TTL.session, query.force);
  },
  async revokeAccountSession(id: string): Promise<void> {
    await request(`/account/sessions/${encodeURIComponent(id)}`, { method: 'DELETE' });
    invalidateCache('account-sessions:');
  },
  async jobs(query: MediaListQuery = {}): Promise<JobListResponse> {
    const path = listPath('/jobs', query);
    return cachedRequest(`jobs:${path}`, () => request<JobListResponse>(path), CACHE_TTL.jobs, query.force);
  },
  async datasets(query: MediaListQuery = {}): Promise<PaginatedResponse<JobListItem>> {
    const path = listPath('/datasets', query);
    return cachedRequest(`datasets:${path}`, () => request<PaginatedResponse<JobListItem>>(path), CACHE_TTL.datasets, query.force);
  },
  async mediaJob(id: string, signal?: AbortSignal): Promise<MediaJob> {
    const job = await request<MediaJob>(`/jobs/${id}`, { signal });
    for (const prefix of ['jobs:', 'datasets:']) {
      updateCachedValues<PaginatedResponse<JobListItem>>(prefix, (page) => ({
        ...page,
        items: page.items.map((item) => item.jobId === job.jobId ? { ...item, ...job } : item),
      }));
    }
    return job;
  },
  async reprocessMedia(id: string): Promise<MediaUploadResponse> {
    const result = await request<MediaUploadResponse>(`/raw-media/${id}/reprocess`, {
      method: 'POST',
      body: JSON.stringify({ reason: 'Requested from workspace portal' }),
    });
    invalidateCache('jobs:');
    invalidateCache('datasets:');
    return result;
  },
  async assignMedia(id: string, assignedTo?: string): Promise<void> {
    await request(`/raw-media/${id}/assignment`, {
      method: 'PATCH',
      body: JSON.stringify({ assignedTo: assignedTo || null }),
    });
    updateCachedValues<PaginatedResponse<JobListItem>>('jobs:', (page) => ({ ...page, items: page.items.map((job) => job.rawMediaId === id ? { ...job, assignedTo } : job) }));
    updateCachedValues<PaginatedResponse<JobListItem>>('datasets:', (page) => ({ ...page, items: page.items.map((job) => job.rawMediaId === id ? { ...job, assignedTo } : job) }));
  },
  async users(query: UserListQuery = {}): Promise<UserListResponse> {
    const path = listPath('/users', query);
    return cachedRequest(`users:${path}`, () => request<UserListResponse>(path), CACHE_TTL.users, query.force);
  },
  async userOptions(force = false): Promise<UserOption[]> {
    return cachedRequest('user-options', () => request<UserOption[]>('/users/options'), CACHE_TTL.users, force);
  },
  async createUser(input: CreateUserInput): Promise<CreatedWorkspaceUser> {
    const created = await request<CreatedWorkspaceUser>('/users', { method: 'POST', body: JSON.stringify(input) });
    invalidateCache('users:');
    invalidateCache('user-options');
    return created;
  },
  async updateUser(id: string, input: UpdateUserInput): Promise<WorkspaceUser> {
    const updated = await request<WorkspaceUser>(`/users/${id}`, { method: 'PATCH', body: JSON.stringify(input) });
    invalidateCache('users:');
    invalidateCache('user-options');
    return updated;
  },
  async permissions(options?: { force?: boolean }): Promise<PermissionConfiguration[]> {
    return cachedRequest('permissions', () => request<PermissionConfiguration[]>('/permissions'), CACHE_TTL.permissions, options?.force);
  },
  async createPermission(input: CreatePermissionInput): Promise<PermissionConfiguration> {
    const created = await request<PermissionConfiguration>('/permissions', { method: 'POST', body: JSON.stringify(input) });
    updateCachedValues<PermissionConfiguration[]>('permissions', (permissions) => [...permissions, created].sort((a, b) => a.key.localeCompare(b.key)));
    invalidateCache('users');
    return created;
  },
  async updatePermission(key: string, input: UpdatePermissionInput): Promise<PermissionConfiguration> {
    const updated = await request<PermissionConfiguration>(`/permissions/${encodeURIComponent(key)}`, { method: 'PATCH', body: JSON.stringify(input) });
    updateCachedValues<PermissionConfiguration[]>('permissions', (permissions) => permissions.map((item) => item.key === updated.key ? updated : item));
    invalidateCache('users');
    return updated;
  },
  async deletePermission(key: string): Promise<void> {
    await request(`/permissions/${encodeURIComponent(key)}`, { method: 'DELETE' });
    updateCachedValues<PermissionConfiguration[]>('permissions', (permissions) => permissions.filter((item) => item.key !== key));
    invalidateCache('users');
  },
  async tenants(force = false): Promise<TenantOption[]> {
    return cachedRequest('tenants', () => request<TenantOption[]>('/users/tenants'), CACHE_TTL.users, force);
  },
  async uploadMedia(file: File, kind: MediaUploadKind, onProgress?: (progress: number) => void, targetTenantId?: string, metadata?: UploadSubmissionMetadata): Promise<MediaUploadResponse | ArchiveUploadResponse> {
    const result = await uploadMediaFile(file, kind, onProgress, true, targetTenantId, metadata);
    invalidateCache('jobs:');
    invalidateCache('datasets:');
    return result;
  },
  async downloadProcessedMedia(id: string): Promise<void> {
    const response = await processedMediaResponse(id);
    const blobUrl = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadFilename(response.headers.get('content-disposition'));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  },
  async downloadProtectedDataset(id: string): Promise<void> {
    const response = await processedMediaResponse(id, true, 'dataset-download');
    const blobUrl = URL.createObjectURL(await response.blob());
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadFilename(response.headers.get('content-disposition'));
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  },
  async processedMediaBlob(id: string): Promise<Blob> {
    return (await processedMediaResponse(id)).blob();
  },
  async processedMediaPreview(id: string): Promise<ProcessedDocumentPreview> {
    return request<ProcessedDocumentPreview>(`/processed-media/${encodeURIComponent(id)}/preview`);
  },
  async processedMediaReport(id: string): Promise<any> {
    return request<any>(`/processed-media/${encodeURIComponent(id)}/report`);
  },

  // ---------------------------------------------------------------------------
  // Generic Data Vault & Connectors
  // ---------------------------------------------------------------------------
  async connectors(force = false): Promise<DataSource[]> {
    return cachedRequest('connectors', () => request<DataSource[]>('/connectors'), CACHE_TTL.connectors, force);
  },
  async connections(force = false): Promise<SourceConnection[]> {
    return cachedRequest('connections', () => request<SourceConnection[]>('/connections'), CACHE_TTL.connections, force);
  },
  async createConnection(input: CreateConnectionInput): Promise<SourceConnection> {
    const result = await request<SourceConnection>('/connections', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    invalidateCache('connections');
    return result;
  },
  async updateConnection(id: string, input: UpdateConnectionInput): Promise<SourceConnection> {
    const result = await request<SourceConnection>(`/connections/${encodeURIComponent(id)}`, {
      method: 'PUT',
      body: JSON.stringify(input),
    });
    invalidateCache('connections');
    return result;
  },
  async deleteConnection(id: string): Promise<void> {
    await request(`/connections/${encodeURIComponent(id)}`, { method: 'DELETE' });
    invalidateCache('connections');
  },
  async testConnectionTransient(sourceId: string, config: Record<string, any>): Promise<ConnectionTestResult> {
    return request<ConnectionTestResult>('/connections/test', {
      method: 'POST',
      body: JSON.stringify({ sourceId, config }),
    });
  },
  async testConnection(id: string): Promise<ConnectionTestResult> {
    return request<ConnectionTestResult>(`/connections/${encodeURIComponent(id)}/test`, {
      method: 'POST',
    });
  },
  async syncConnection(id: string, datasetId?: string): Promise<IngestionJob> {
    const url = datasetId
      ? `/connections/${encodeURIComponent(id)}/sync?dataset_id=${encodeURIComponent(datasetId)}`
      : `/connections/${encodeURIComponent(id)}/sync`;
    const result = await request<IngestionJob>(url, { method: 'POST' });
    invalidateCache('connections');
    invalidateCache('ingestion-jobs:');
    invalidateCache('vault:datasets');
    return result;
  },
  async vaultDatasets(force = false): Promise<VaultDataset[]> {
    return cachedRequest('vault:datasets', () => request<VaultDataset[]>('/vault/datasets'), CACHE_TTL.vault, force);
  },
  async createVaultDataset(input: CreateDatasetInput): Promise<VaultDataset> {
    const result = await request<VaultDataset>('/vault/datasets', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    invalidateCache('vault:datasets');
    return result;
  },
  async getVaultDataset(id: string): Promise<VaultDataset> {
    return request<VaultDataset>(`/vault/datasets/${encodeURIComponent(id)}`);
  },
  async getVaultDatasetSchema(id: string, force = false): Promise<DatasetSchema> {
    return cachedRequest(`vault:schema:${id}`, () => request<DatasetSchema>(`/vault/datasets/${encodeURIComponent(id)}/schema`), CACHE_TTL.vault, force);
  },
  async updateVaultDatasetSchema(id: string, fields: SchemaFieldDefinition[]): Promise<DatasetSchema> {
    const result = await request<DatasetSchema>(`/vault/datasets/${encodeURIComponent(id)}/schema`, {
      method: 'PUT',
      body: JSON.stringify({ fields }),
    });
    invalidateCache(`vault:schema:${id}`);
    return result;
  },
  async vaultRecords(
    datasetId: string,
    query: { page?: number; pageSize?: number; search?: string; force?: boolean } = {}
  ): Promise<PaginatedResponse<VaultRecordItem>> {
    const params = new URLSearchParams();
    if (query.page) params.set('page', String(query.page));
    if (query.pageSize) params.set('page_size', String(query.pageSize));
    if (query.search) params.set('search', query.search);
    const qs = params.toString();
    const path = `/vault/datasets/${encodeURIComponent(datasetId)}/records${qs ? `?${qs}` : ''}`;
    return cachedRequest(`vault:records:${path}`, () => request<PaginatedResponse<VaultRecordItem>>(path), CACHE_TTL.vault, query.force);
  },
  async revealVaultField(
    datasetId: string,
    recordId: string,
    input: RevealFieldInput
  ): Promise<RevealFieldResult> {
    return request<RevealFieldResult>(
      `/vault/datasets/${encodeURIComponent(datasetId)}/records/${encodeURIComponent(recordId)}/reveal`,
      {
        method: 'POST',
        body: JSON.stringify(input),
      }
    );
  },
  exportVaultDatasetUrl(datasetId: string, format: 'json' | 'csv' = 'json'): string {
    const token = typeof sessionStorage !== 'undefined' ? sessionStorage.getItem('privacy_factory_access_token') || '' : '';
    const tokenParam = token ? `&token=${encodeURIComponent(token)}` : '';
    return `${baseUrl}/vault/datasets/${encodeURIComponent(datasetId)}/export?format=${format}${tokenParam}`;
  },
  async downloadVaultDatasetExport(datasetId: string, format: 'json' | 'csv' = 'json', retry = true): Promise<void> {
    const generation = authenticationGeneration;
    const response = await fetch(`${baseUrl}/vault/datasets/${encodeURIComponent(datasetId)}/export?format=${format}`, {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (response.status === 401 && retry) {
      try {
        await refreshAuthentication();
        return this.downloadVaultDatasetExport(datasetId, format, false);
      } catch {
        expireAuthentication();
      }
    }
    if (!response.ok) throw await parseError(response);
    if (generation !== authenticationGeneration) {
      throw new ApiError('The authentication state changed while the download was in progress.', 'AUTH_STATE_CHANGED', 401);
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadFilename(response.headers.get('content-disposition')) || `dataset_export.${format}`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 0);
  },
  async importVaultDatasetCsv(
    datasetId: string,
    file: File,
    targetTenantId?: string
  ): Promise<{ success: boolean; filename: string; recordsImported: number; totalDatasetRecords: number }> {
    const formData = new FormData();
    formData.append('file', file);
    const headers: Record<string, string> = {};
    if (targetTenantId) headers['X-Target-Tenant-Id'] = targetTenantId;
    const queryParams = targetTenantId ? `?target_tenant_id=${encodeURIComponent(targetTenantId)}` : '';
    const result = await request<{ success: boolean; filename: string; recordsImported: number; totalDatasetRecords: number }>(
      `/vault/datasets/${encodeURIComponent(datasetId)}/import-csv${queryParams}`,
      {
        method: 'POST',
        headers,
        body: formData,
      }
    );
    invalidateCache('vault:datasets');
    invalidateCache(`vault:records:/vault/datasets/${encodeURIComponent(datasetId)}`);
    return result;
  },
  async ingestionJobs(
    query: { datasetId?: string; status?: string; page?: number; pageSize?: number; force?: boolean } = {}
  ): Promise<PaginatedResponse<IngestionJob>> {
    const params = new URLSearchParams();
    if (query.datasetId) params.set('dataset_id', query.datasetId);
    if (query.status) params.set('status', query.status);
    if (query.page) params.set('page', String(query.page));
    if (query.pageSize) params.set('page_size', String(query.pageSize));
    const qs = params.toString();
    const path = `/ingestion-jobs${qs ? `?${qs}` : ''}`;
    return cachedRequest(`ingestion-jobs:${path}`, () => request<PaginatedResponse<IngestionJob>>(path), CACHE_TTL.vault, query.force);
  },

  // -------------------------------------------------------------------------
  // Freelancer Acquisition & Client Publication
  // -------------------------------------------------------------------------
  async registerFreelancer(payload: { email: string; password: string; displayName: string; tenantName?: string }): Promise<AuthResponse> {
    const response = await fetch(`${baseUrl}/auth/register-freelancer`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(payload),
    });
    if (!response.ok) throw await parseError(response);
    return saveAuthentication(await response.json(), true);
  },

  async acquisitionAgreementActive(force = false): Promise<AgreementResponse> {
    return cachedRequest('acquisition:agreement:active', () => request<AgreementResponse>('/acquisition/agreements/active'), CACHE_TTL.permissions, force);
  },

  async acquisitionAgreementPdf(): Promise<Blob> {
    const response = await fetch(`${baseUrl}/acquisition/agreements/active/pdf`, {
      credentials: 'include',
      headers: authHeaders(),
    });
    if (!response.ok) throw await parseError(response);
    return response.blob();
  },

  async acquisitionAgreementStatus(force = false): Promise<AgreementStatusResponse> {
    return cachedRequest('acquisition:agreement:status', () => request<AgreementStatusResponse>('/acquisition/agreements/status'), 10_000, force);
  },

  async acquisitionAgreementAccept(payload: AgreementAcceptanceRequest): Promise<AgreementAcceptanceResponse> {
    const result = await request<AgreementAcceptanceResponse>('/acquisition/agreements/accept', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    invalidateCache('acquisition:agreement:status');
    return result;
  },

  async acquisitionSubmissionsMine(force = false): Promise<FreelancerSubmissionResponse[]> {
    return cachedRequest('acquisition:submissions:mine', () => request<FreelancerSubmissionResponse[]>('/acquisition/submissions/mine'), CACHE_TTL.datasets, force);
  },

  async acquisitionSubmissionDetail(id: string): Promise<FreelancerSubmissionResponse> {
    return request<FreelancerSubmissionResponse>(`/acquisition/submissions/${encodeURIComponent(id)}`);
  },

  async acquisitionCreateSubmission(formData: FormData, onProgress?: (progress: number) => void): Promise<SubmissionCreateResponse> {
    return uploadAcquisitionSubmission(formData, onProgress);
  },

  async acquisitionReviseSubmission(submissionId: string, formData: FormData): Promise<SubmissionCreateResponse> {
    const headers = authHeaders();
    const response = await fetch(`${baseUrl}/acquisition/submissions/${encodeURIComponent(submissionId)}/revise`, {
      method: 'POST',
      headers,
      body: formData,
    });
    if (!response.ok) throw await parseError(response);
    invalidateCache('acquisition:submissions:mine');
    invalidateCache('acquisition:superadmin:queue');
    invalidateCache('acquisition:qc:queue');
    return response.json();
  },

  async acquisitionSuperadminQueue(force = false): Promise<FreelancerSubmissionResponse[]> {
    return cachedRequest('acquisition:superadmin:queue', () => request<FreelancerSubmissionResponse[]>('/acquisition/superadmin/queue'), CACHE_TTL.datasets, force);
  },

  async acquisitionSuperadminAssign(submissionId: string, reviewerId: string): Promise<FreelancerSubmissionResponse> {
    const result = await request<FreelancerSubmissionResponse>(`/acquisition/superadmin/submissions/${encodeURIComponent(submissionId)}/assign`, {
      method: 'POST',
      body: JSON.stringify({ reviewerId }),
    });
    invalidateCache('acquisition:superadmin:queue');
    invalidateCache('acquisition:qc:queue');
    return result;
  },

  async acquisitionSuperadminReviewers(): Promise<Array<{ id: string; name: string; email: string; roles: string }>> {
    return request<Array<{ id: string; name: string; email: string; roles: string }>>('/acquisition/superadmin/reviewers');
  },

  async acquisitionSuperadminClients(): Promise<Array<{ tenantId: string; tenantName: string }>> {
    return request<Array<{ tenantId: string; tenantName: string }>>('/acquisition/superadmin/clients');
  },

  async acquisitionQcQueue(force = false): Promise<FreelancerSubmissionResponse[]> {
    return cachedRequest('acquisition:qc:queue', () => request<FreelancerSubmissionResponse[]>('/acquisition/qc/queue'), CACHE_TTL.datasets, force);
  },

  async acquisitionQcSubmitReview(submissionId: string, versionId: string, payload: QcReviewSubmitRequest): Promise<QcReviewResponse> {
    const result = await request<QcReviewResponse>(`/acquisition/qc/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/review`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    invalidateCache('acquisition:superadmin:queue');
    invalidateCache('acquisition:qc:queue');
    invalidateCache('acquisition:submissions:mine');
    return result;
  },

  async acquisitionDownloadSubmissionVersion(
    submissionId: string,
    versionId: string,
    filename: string,
    protectedDownload: boolean = false
  ): Promise<void> {
    const token = sessionStorage.getItem('privacy_factory_access_token');
    const tenantId = sessionStorage.getItem('privacy_factory_tenant_id');
    const query = protectedDownload ? '?protected=true' : '';
    const res = await fetch(`${baseUrl}/acquisition/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/download${query}`, {
      headers: {
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(tenantId ? { 'x-tenant-id': tenantId } : {}),
      },
    });
    if (!res.ok) {
      const err = await parseError(res);
      throw err;
    }
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'dataset.bin';
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  async aiSettings(): Promise<AiSettings> {
    return request<AiSettings>('/analysis/ai-settings');
  },

  async saveAiSettings(input: AiSettingsInput): Promise<AiSettings> {
    return request<AiSettings>('/analysis/ai-settings', { method: 'PUT', body: JSON.stringify(input) });
  },

  async testAiSettings(input: AiSettingsInput): Promise<AiSettingsTestResult> {
    return request<AiSettingsTestResult>('/analysis/ai-settings/test', { method: 'POST', body: JSON.stringify(input) });
  },

  async aiModels(input: AiSettingsInput): Promise<string[]> {
    const result = await request<{ models: string[] }>('/analysis/ai-settings/models', {
      method: 'POST',
      body: JSON.stringify(input),
    });
    return result.models;
  },

  async acquisitionVersionAnalysis(submissionId: string, versionId: string): Promise<VersionAnalysisResponse> {
    return request<VersionAnalysisResponse>(
      `/acquisition/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/analysis`
    );
  },

  async acquisitionRerunAnalysis(submissionId: string, versionId: string): Promise<VersionAnalysisResponse> {
    const result = await request<VersionAnalysisResponse>(
      `/acquisition/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/analysis/rerun`,
      { method: 'POST' }
    );
    invalidateCache('acquisition:qc:queue');
    invalidateCache('acquisition:superadmin:queue');
    return result;
  },

  async acquisitionDownloadAnalysisReport(submissionId: string, versionId: string, filename: string): Promise<void> {
    const res = await fetch(
      `${baseUrl}/acquisition/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/analysis/report`,
      { headers: authHeaders(), credentials: 'include' }
    );
    if (!res.ok) throw await parseError(res);
    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    window.URL.revokeObjectURL(url);
    document.body.removeChild(a);
  },

  async acquisitionGetVersionBlob(
    submissionId: string,
    versionId: string,
    protectedDownload: boolean = false
  ): Promise<Blob> {
    const headers = authHeaders();
    const query = protectedDownload ? '?protected=true' : '';
    const res = await fetch(`${baseUrl}/acquisition/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/download${query}`, {
      headers,
    });
    if (!res.ok) throw await parseError(res);
    return res.blob();
  },

  async acquisitionPreviewSubmissionVersion(
    submissionId: string,
    versionId: string,
    raw: boolean = true
  ): Promise<any> {
    const q = raw ? '?raw=true' : '?raw=false';
    return request<any>(`/acquisition/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/preview${q}`);
  },

  async acquisitionVerifyRights(submissionId: string, versionId: string, payload: RightsVerificationRequest): Promise<RightsVerificationResponse> {
    const result = await request<RightsVerificationResponse>(`/acquisition/superadmin/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/verify-rights`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    invalidateCache('acquisition:superadmin:queue');
    invalidateCache('acquisition:qc:queue');
    return result;
  },

  async acquisitionProtectVersion(submissionId: string, versionId: string): Promise<SubmissionVersionResponse> {
    const result = await request<SubmissionVersionResponse>(`/acquisition/superadmin/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/protect`, {
      method: 'POST',
    });
    invalidateCache('acquisition:superadmin:queue');
    invalidateCache('acquisition:qc:queue');
    invalidateCache('acquisition:submissions:mine');
    return result;
  },

  async acquisitionPublishToClient(submissionId: string, versionId: string, payload: PublishToClientRequest): Promise<ClientPublicationResponse> {
    const result = await request<ClientPublicationResponse>(`/acquisition/superadmin/submissions/${encodeURIComponent(submissionId)}/versions/${encodeURIComponent(versionId)}/publish`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    invalidateCache('acquisition:superadmin:queue');
    invalidateCache('acquisition:qc:queue');
    invalidateCache('acquisition:client:catalog');
    return result;
  },

  async acquisitionClientCatalog(force = false, search?: string): Promise<ClientCatalogItem[]> {
    const query = search && search.trim() ? `?search=${encodeURIComponent(search.trim())}` : '';
    const key = `acquisition:client:catalog${query}`;
    return cachedRequest(key, () => request<ClientCatalogItem[]>(`/acquisition/catalog${query}`), CACHE_TTL.datasets, force);
  },

  async acquisitionCatalogPreview(publicationId: string): Promise<any> {
    return request<any>(`/acquisition/catalog/${encodeURIComponent(publicationId)}/preview`);
  },

  async acquisitionCatalogDownloadUrl(publicationId: string): Promise<string> {
    const res = await request<{ downloadUrl: string }>(`/acquisition/catalog/${encodeURIComponent(publicationId)}/download`);
    let downloadUrl = res.downloadUrl;
    if (downloadUrl.startsWith('/api') && baseUrl.startsWith('http')) {
      const origin = new URL(baseUrl).origin;
      downloadUrl = `${origin}${downloadUrl}`;
    }
    return downloadUrl;
  },

  async downloadCatalogDataset(publicationId: string, customFilename?: string): Promise<void> {
    const downloadUrl = await this.acquisitionCatalogDownloadUrl(publicationId);
    const response = await fetch(downloadUrl, { headers: authHeaders() });
    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      throw new Error(err.message || 'The dataset file could not be downloaded.');
    }
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = downloadFilename(response.headers.get('content-disposition')) || customFilename || 'dataset_package.bin';
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  },

  async acquisitionBatchPublishToClient(targetClientIdentifier: string = 'ibm'): Promise<{
    status: string;
    targetTenantId: string;
    targetTenantName: string;
    totalEvaluated: number;
    newlyPublished: number;
    updatedPublished: number;
    publishedTitles: string[];
  }> {
    return request('/acquisition/superadmin/publish-media-to-client', {
      method: 'POST',
      body: JSON.stringify({ targetClientIdentifier }),
    });
  },

  async acquisitionNotifications(): Promise<AcquisitionNotificationItem[]> {
    return request<AcquisitionNotificationItem[]>('/acquisition/notifications');
  },

  async acquisitionMarkNotificationRead(notificationId: string): Promise<void> {
    await request<void>(`/acquisition/notifications/${encodeURIComponent(notificationId)}/read`, { method: 'PATCH' });
  },

  // ---------------------------------------------------------------------------
  // Retail Labeled Dataset Platform
  // ---------------------------------------------------------------------------
  async retailListStores(): Promise<RetailStore[]> {
    return request<RetailStore[]>('/retail/stores');
  },

  async retailCreateStore(payload: { store_code: string; name: string; store_format?: string; region?: string; timezone?: string }): Promise<RetailStore> {
    return request<RetailStore>('/retail/stores', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async retailListAssets(filters?: { store_id?: string; lighting?: string; is_weekend?: boolean; status?: string }): Promise<RetailAsset[]> {
    const params = new URLSearchParams();
    if (filters?.store_id) params.set('store_id', filters.store_id);
    if (filters?.lighting) params.set('lighting', filters.lighting);
    if (filters?.is_weekend !== undefined) params.set('is_weekend', String(filters.is_weekend));
    if (filters?.status) params.set('status', filters.status);
    const query = params.toString() ? `?${params.toString()}` : '';
    return request<RetailAsset[]>(`/retail/assets${query}`);
  },

  async retailInitiateUpload(payload: {
    store_id: string;
    filename: string;
    aisle_id: string;
    camera_id: string;
    lighting_condition?: string;
    resolution?: string;
    is_weekend?: boolean;
    modality?: string;
  }): Promise<{ asset_id: string; upload_url: string; raw_storage_path: string; expires_at: string }> {
    return request('/retail/assets/initiate-upload', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async retailFinalizeUpload(assetId: string): Promise<{ id: string; status: string; raw_storage_path: string }> {
    return request(`/retail/assets/${encodeURIComponent(assetId)}/finalize-upload`, {
      method: 'POST',
    });
  },

  async retailUploadDirect(formData: FormData): Promise<{ id: string; status: string; raw_storage_path: string; resolution?: string }> {
    return request('/retail/assets/upload-direct', {
      method: 'POST',
      body: formData,
    });
  },

  async retailAssignTask(id:string,annotatorId:string):Promise<unknown>{
    return request(`/retail/tasks/${encodeURIComponent(id)}/assign`,{method:'POST',body:JSON.stringify({annotator_id:annotatorId})});
  },
  async retailPrivacyReport(id:string):Promise<RetailPrivacyInspection>{
    return request(`/retail/assets/${encodeURIComponent(id)}/privacy-report`);
  },
  async retailApprovePrivacy(id:string,payload:{checks:Record<string,boolean>;notes:string;protected_sha256:string}):Promise<unknown>{
    return request(`/retail/assets/${encodeURIComponent(id)}/privacy-review`,{method:'POST',body:JSON.stringify(payload)});
  },
  async retailQuickApprovePrivacy(assetId: string, notes?: string): Promise<{ id: string; status: string; pii_cleared: boolean; pii_audit_hash: string | null }> {
    return request(`/retail/assets/${encodeURIComponent(assetId)}/approve-privacy`, {
      method: 'POST',
      body: JSON.stringify({ notes: notes || 'Verified PII protection by QC reviewer.' }),
    });
  },
  async retailGetCatalog(id:string): Promise<RetailCatalog> {
    return request(`/retail/assets/${encodeURIComponent(id)}/catalog`);
  },
  async retailInspectCatalog(id:string): Promise<RetailCatalog> {
    return request(`/retail/assets/${encodeURIComponent(id)}/catalog/inspect`,{method:'POST'});
  },
  async retailUpdateCatalogContext(id:string,payload:{camera_id:string;aisle_id:string;camera_angle:string;captured_at:string;lighting:string}): Promise<RetailCatalog> {
    return request(`/retail/assets/${encodeURIComponent(id)}/catalog/context`,{method:'PATCH',body:JSON.stringify(payload)});
  },
  async retailNormalizeAsset(assetId: string, blurRegions?: { xmin: number; ymin: number; xmax: number; ymax: number }[]): Promise<{
    id: string;
    status: string;
    quality_score: number | null;
    blur_metric: number | null;
    pii_cleared: boolean;
    protected_storage_path: string | null;
    thumbnail_storage_path: string | null;
    pii_audit_hash: string | null;
  }> {
    return request(`/retail/assets/${encodeURIComponent(assetId)}/normalize`, {
      method: 'POST',
      body: JSON.stringify({ blur_regions: blurRegions || [] }),
    });
  },

  async retailBulkSkus(skus: Array<{ sku_code: string; brand_name: string; category: string; upc_barcode?: string }>): Promise<{ processed: number }> {
    return request('/retail/catalog/skus', {
      method: 'POST',
      body: JSON.stringify({ skus }),
    });
  },

  async retailImportCatalogZip(file:File,license='OWNER_RIGHTS_CONFIRMED'):Promise<any>{const form=new FormData();form.append('file',file);form.append('license',license);form.append('source_name',file.name);form.append('rights_confirmed','true');return request('/retail/catalog/import-zip',{method:'POST',body:form});},
  async retailImportOpenFoodFacts(payload:{brand:string|null;country:string|null;barcodes:string[];limit:number}):Promise<any>{return request('/retail/catalog/sources/open-food-facts',{method:'POST',body:JSON.stringify(payload)});},
  async retailSeedStarterCatalog():Promise<any>{return request('/retail/catalog/seed-starter',{method:'POST'});},
  async retailExternalSources():Promise<{sources:any[]}>{return request('/retail/catalog/external-sources');},
  async retailCategories():Promise<{categories:string[]}>{return request('/retail/catalog/retail-categories');},
  async retailUploadSkuPhotos(skuId:string,files:File[]):Promise<any>{const form=new FormData();files.forEach(f=>form.append('files',f));return request(`/retail/catalog/skus/${skuId}/reference-images`,{method:'POST',body:form});},
  async retailRegisterCatalogMatcher(contractId:string,payload:{image_threshold:number;tiling:boolean;min_proposals:number}):Promise<any>{return request(`/retail/contracts/${contractId}/catalog-matcher`,{method:'POST',body:JSON.stringify(payload)});},
  async retailListSkus(): Promise<Array<{ id: string; sku_code: string; brand_name: string; category: string; upc_barcode?: string; reference_images?: number }>> {
    return request('/retail/catalog/skus');
  },

  async retailListContracts(): Promise<LabelContract[]> {
    return request<LabelContract[]>('/retail/contracts');
  },

  async retailCreateContract(payload: {
    name: string;
    version?: string;
    annotation_types?: string[];
    taxonomy_classes?: Array<{ class_id: string; name: string; brand?: string; category?: string }>;
    attribute_rules?: Record<string, unknown>;
    guidelines_markdown?: string;
  }): Promise<LabelContract> {
    return request<LabelContract>('/retail/contracts', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async retailGetCoverage(): Promise<SamplingCoverage> {
    return request<SamplingCoverage>('/retail/sampling/coverage');
  },

  async retailGenerateBatch(payload: {
    contract_id: string;
    batch_size?: number;
    gold_ratio?: number;
    target_store_id?: string;
  }): Promise<TaskBatchResponse> {
    return request<TaskBatchResponse>('/retail/sampling/generate-batch', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async retailSubmitTask(taskId: string, payload: {
    annotations: Array<{ class_name: string; bbox: number[]; attributes?: Record<string, unknown> }>;
    time_spent_seconds?: number;
  }): Promise<{ submission_id: string; task_id: string; qc_geometry_passed: boolean; qc_geometry_errors: string[]; gold_iou_score: number | null }> {
    return request(`/retail/tasks/${encodeURIComponent(taskId)}/submit`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async retailReviewTask(taskId: string, payload: {
    submission_id?: string;
    verdict: string;
    feedback_notes?: string;
    iou_agreement?: number;
  }): Promise<{ review_id: string; verdict: string; task_status: string }> {
    return request(`/retail/tasks/${encodeURIComponent(taskId)}/review`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async retailAnnotatorMetrics(annotatorId: string): Promise<Record<string, unknown>> {
    return request(`/retail/annotators/${encodeURIComponent(annotatorId)}/metrics`);
  },

  async retailListTasks(status?: string): Promise<Array<{
    id: string;
    contract_id: string;
    asset_id: string;
    batch_id: string | null;
    status: string;
    is_gold_task: boolean;
    target_split: string;
    submissions_count: number;
    created_at?: string;
  }>> {
    const q = status ? `?status=${encodeURIComponent(status)}` : '';
    return request(`/retail/tasks${q}`);
  },

  async retailBatchManifest(taskIds: string[]): Promise<Record<string, unknown>> {
    return request('/retail/model-in-the-loop/batch-manifest', {
      method: 'POST',
      body: JSON.stringify(taskIds),
    });
  },

  async retailIngestPredictions(predictions: Array<{ task_id: string; confidence?: number; annotations: Array<{ class_name: string; bbox: number[]; attributes?: Record<string, unknown> }> }>): Promise<{
    total_processed: number;
    high_confidence_prelabeled: number;
    correction_mode_enqueued: number;
    active_learning_hard_cases: number;
  }> {
    return request('/retail/model-in-the-loop/ingest-predictions', {
      method: 'POST',
      body: JSON.stringify({ predictions }),
    });
  },

  async retailCreateRelease(payload: { name: string; version: string; contract_id: string }): Promise<DatasetReleaseItem> {
    return request<DatasetReleaseItem>('/retail/releases', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

  async retailLockSplits(releaseId: string): Promise<{ release_id: string; stats: Record<string, unknown> }> {
    return request(`/retail/releases/${encodeURIComponent(releaseId)}/lock-splits`, {
      method: 'POST',
    });
  },

  async retailPackageDelivery(releaseId: string): Promise<{
    release_id: string;
    version: string;
    status: string;
    manifest_sha256: string;
    package_path: string;
    download_url: string;
    expires_at: string;
  }> {
    return request(`/retail/releases/${encodeURIComponent(releaseId)}/package-delivery`, {
      method: 'POST',
    });
  },

  async retailListReleases(): Promise<DatasetReleaseItem[]> {
    return request<DatasetReleaseItem[]>('/retail/releases');
  },

  async retailDownloadFile(path:string,filename:string,retry=true):Promise<void>{
    // Same auth handling as API calls: an expired access token is refreshed once, then the download is retried.
    const response=await fetch(path,{credentials:'include',headers:{...authHeaders()}});
    if(response.status===401&&retry){await refreshAuthentication();return this.retailDownloadFile(path,filename,false);}
    if(!response.ok)throw new Error(`Download failed (${response.status})`);
    const url=URL.createObjectURL(await response.blob());const a=document.createElement('a');a.href=url;a.download=filename;a.click();setTimeout(()=>URL.revokeObjectURL(url),1000);
  },
  async retailDatasetFlow(assetId:string):Promise<any>{return request(`/retail/assets/${assetId}/dataset-flow`);},
  async retailConfigureDatasetFlow(assetId:string,payload:Record<string,unknown>):Promise<any>{return request(`/retail/assets/${assetId}/dataset-flow`,{method:'POST',body:JSON.stringify(payload)});},
  async retailContinueDatasetFlow(assetId:string):Promise<any>{return request(`/retail/assets/${assetId}/dataset-flow/continue`,{method:'POST'});},
  async retailUploadProductModel(contractId:string,file:File,inputSize:number):Promise<any>{const form=new FormData();form.append('file',file);form.append('input_size',String(inputSize));return request(`/retail/contracts/${contractId}/product-model`,{method:'POST',body:form});},
  async retailFlowApproveAll(assetId:string,notes:string):Promise<any>{return request(`/retail/assets/${assetId}/dataset-flow/approve-all`,{method:'POST',body:JSON.stringify({notes})});},
  async retailRerunFlowModel(assetId:string):Promise<any>{return request(`/retail/assets/${assetId}/dataset-flow/rerun-model`,{method:'POST'});},
  async retailWorkspaceReadiness():Promise<any>{return request('/retail/workspace-readiness');},
  async retailSetAutoFlow(contractId:string,payload:{enabled:boolean;max_units:number;pilot_train_only:boolean;annotation_mode:string}):Promise<any>{return request(`/retail/contracts/${contractId}/auto-flow`,{method:'POST',body:JSON.stringify(payload)});},
  async retailFlowPropagate(assetId:string,taskId:string):Promise<any>{return request(`/retail/assets/${assetId}/dataset-flow/tasks/${taskId}/propagate`,{method:'POST'});},
  async retailRegisterHfModel(contractId:string,payload:{repo_id:string;file:string;threshold:number;tiling?:boolean;min_proposals?:number}):Promise<any>{return request(`/retail/contracts/${contractId}/hf-model`,{method:'POST',body:JSON.stringify(payload)});},
  async retailFlowSubmit(assetId:string,taskId:string,annotations:unknown[],seconds:number):Promise<any>{return request(`/retail/assets/${assetId}/dataset-flow/tasks/${taskId}/submit`,{method:'POST',body:JSON.stringify({annotations,time_spent_seconds:seconds})});},
  async retailFlowReview(assetId:string,taskId:string,submissionId:string,notes:string):Promise<any>{return request(`/retail/assets/${assetId}/dataset-flow/tasks/${taskId}/review`,{method:'POST',body:JSON.stringify({submission_id:submissionId,verdict:'APPROVED',feedback_notes:notes})});},
  async retailFlowImport(assetId:string,predictions:unknown[]):Promise<any>{return request(`/retail/assets/${assetId}/dataset-flow/predictions`,{method:'POST',body:JSON.stringify({predictions})});},

  async retailStartAutomatedQc(assetId: string): Promise<any> {
    return request(`/retail/assets/${assetId}/automated-qc`, {method:'POST'});
  },
  async retailGetAutomatedQc(assetId: string): Promise<any> {
    return request(`/retail/assets/${assetId}/automated-qc`);
  },

  async retailLaunchPipeline(formData: FormData): Promise<{
    success: boolean;
    release_id: string;
    release_name: string;
    release_version: string;
    download_url: string;
    manifest_sha256: string;
    expires_at: string;
    files_processed: number;
    pii_cleared_count: number;
    total_boxes_labeled: number;
    splits: Record<string, number>;
    contract_name: string;
    pipeline_log: string[];
    elapsed_seconds: number;
  }> {
    return request('/retail/pipeline/launch', {
      method: 'POST',
      body: formData,
    });
  },

  async retailQcPendingQueue(): Promise<any[]> {
    return request<any[]>('/retail/qc/pending-queue');
  },

  async retailPublishReleaseToClient(releaseId: string, payload: { target_client_tenant_id: string; notes?: string }): Promise<{
    release_id: string;
    release_name: string;
    version: string;
    status: string;
    target_client_tenant_id: string;
    target_client_name: string;
    download_url: string;
    expires_at: string;
    manifest_sha256: string;
  }> {
    return request(`/retail/releases/${encodeURIComponent(releaseId)}/publish-to-client`, {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },

};

function parseRetryAfter(value: string | null): number | undefined {
  if (!value) return undefined;
  const seconds = Number(value);
  if (Number.isFinite(seconds) && seconds >= 0) return seconds * 1000;
  const date = Date.parse(value);
  if (Number.isNaN(date)) return undefined;
  return Math.max(0, date - Date.now());
}

function downloadFilename(contentDisposition: string | null): string {
  const match = contentDisposition?.match(/filename="?([^";]+)"?/i);
  return match?.[1] || 'protected-media';
}
